// ─────────────────────────────────────────────────────────────────────────────
//  useRoom — the realtime connection to one room's Durable Object.
//
//  A plain WebSocket with an exponential-backoff reconnect loop. Every piece of
//  connection state lives in refs rather than React state, so the loop never
//  reads a stale closure; React state carries only what the UI renders.
//
//  Three things make this survive a real party:
//    • reconnect on close, on error, and on tab-visible (phones kill sockets)
//    • a heartbeat, because an idle WebSocket gets reaped by the edge
//    • rejoin-on-open, so a player who locks their phone comes back as
//      themselves with their score intact
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientMsg, ServerMsg } from "@shared/protocol";
import { CLOSE } from "@shared/protocol";
import type { GameStateView, Role } from "@shared/types";
import { socketUrl } from "./api";
import { emitFx } from "./fxBus";
import { clearPlayerId, getPlayerId, setPlayerId } from "./storage";

export type ConnStatus = "connecting" | "open" | "reconnecting";
export type FatalReason = "notFound" | "badToken" | "full" | "removed";
/** "checking" until the server has told us whether our stored id is still valid. */
export type JoinState = "checking" | "in" | "out";

export interface RoomConnection {
  state: GameStateView | null;
  status: ConnStatus;
  fatal: FatalReason | null;
  playerId: string | null;
  joinState: JoinState;
  send: (msg: ClientMsg) => void;
  /** Forget this device's player identity and present the join form again. */
  leave: () => void;
}

const HEARTBEAT_MS = 25_000;
const BACKOFF_MIN_MS = 500;
const BACKOFF_MAX_MS = 10_000;

function fatalFor(code: number): FatalReason | null {
  switch (code) {
    case CLOSE.ROOM_NOT_FOUND:
      return "notFound";
    case CLOSE.BAD_TOKEN:
      return "badToken";
    case CLOSE.ROOM_FULL:
      return "full";
    case CLOSE.ROOM_DELETED:
      return "removed";
    default:
      return null;
  }
}

export function useRoom(code: string, role: Role, hostToken?: string | null): RoomConnection {
  const [state, setState] = useState<GameStateView | null>(null);
  const [status, setStatus] = useState<ConnStatus>("connecting");
  const [fatal, setFatal] = useState<FatalReason | null>(null);
  const [playerId, setPlayerIdState] = useState<string | null>(() =>
    role === "player" ? getPlayerId(code) : null,
  );
  const [joinState, setJoinState] = useState<JoinState>(() =>
    role !== "player" ? "out" : getPlayerId(code) ? "checking" : "out",
  );

  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const closedByUsRef = useRef(false);
  const playerIdRef = useRef<string | null>(playerId);

  playerIdRef.current = playerId;

  const send = useCallback((msg: ClientMsg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
    // Dropping a message on a dead socket is correct: the server is the source
    // of truth and will re-send state the moment we reconnect.
  }, []);

  const leave = useCallback(() => {
    clearPlayerId(code);
    setPlayerIdState(null);
    setJoinState("out");
  }, [code]);

  useEffect(() => {
    if (!code) return;

    let disposed = false;
    closedByUsRef.current = false;

    const clearTimers = () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      retryTimerRef.current = null;
      heartbeatRef.current = null;
    };

    const scheduleReconnect = () => {
      if (disposed || closedByUsRef.current) return;
      const attempt = attemptRef.current++;
      // Exponential with full jitter: a room full of phones waking at once must
      // not reconnect in lockstep.
      const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** attempt);
      const delay = Math.random() * ceiling;
      setStatus("reconnecting");
      retryTimerRef.current = setTimeout(connect, delay);
    };

    const connect = () => {
      if (disposed) return;
      clearTimers();

      const params = new URLSearchParams({ room: code, role });
      if (role === "host" && hostToken) params.set("token", hostToken);
      const url = socketUrl(params);

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (disposed) return;
        attemptRef.current = 0;
        setStatus("open");
        setFatal(null);

        const stored = playerIdRef.current;
        if (role === "player" && stored) {
          setJoinState("checking");
          ws.send(JSON.stringify({ t: "player:rejoin", playerId: stored } satisfies ClientMsg));
        }

        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ t: "ping" } satisfies ClientMsg));
          }
        }, HEARTBEAT_MS);
      };

      ws.onmessage = (event) => {
        if (disposed || typeof event.data !== "string") return;
        let msg: ServerMsg;
        try {
          msg = JSON.parse(event.data) as ServerMsg;
        } catch {
          return;
        }
        switch (msg.t) {
          case "state":
            setState(msg.state);
            break;
          case "fx":
            emitFx(msg.fx);
            break;
          case "joined":
            setPlayerId(code, msg.playerId);
            setPlayerIdState(msg.playerId);
            setJoinState("in");
            break;
          case "rejoinFailed":
            // The host removed us, or the game was reset from scratch.
            clearPlayerId(code);
            setPlayerIdState(null);
            setJoinState("out");
            break;
          case "error":
            if (msg.code === "auth") setFatal("badToken");
            break;
          case "pong":
            break;
        }
      };

      ws.onclose = (event) => {
        if (disposed) return;
        clearTimers();
        const reason = fatalFor(event.code);
        if (reason) {
          // A fatal close is an answer, not a hiccup — stop retrying and let
          // the page explain what happened.
          setFatal(reason);
          setStatus("reconnecting");
          if (reason === "removed" && role === "player") {
            clearPlayerId(code);
            setPlayerIdState(null);
            setJoinState("out");
          }
          return;
        }
        scheduleReconnect();
      };

      ws.onerror = () => {
        // `onclose` always follows; letting it drive the retry keeps one path.
        try {
          ws.close();
        } catch {
          /* already closing */
        }
      };
    };

    // A phone that was asleep often holds a socket the OS already killed.
    // Coming back to the foreground is the cue to check and reconnect fast.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        attemptRef.current = 0;
        connect();
      }
    };
    const onOnline = () => {
      attemptRef.current = 0;
      connect();
    };

    connect();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    return () => {
      disposed = true;
      closedByUsRef.current = true;
      clearTimers();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(1000, "navigating");
      }
    };
  }, [code, role, hostToken]);

  return { state, status, fatal, playerId, joinState, send, leave };
}
